import { Injectable, Renderer2, RendererFactory2, EventEmitter, DOCUMENT, inject } from '@angular/core';

import { getQueryParam } from '../helpers/url.helper';

export interface ITheme {
  name: string;
  baseColor?: string;
  isActive?: boolean;
}

const DEFAULT_THEME = '';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private document = inject<Document>(DOCUMENT);
  private renderer: Renderer2;

  public onThemeChange: EventEmitter<ITheme> = new EventEmitter();

  /**
   * The historical property name is kept for callers while the options now
   * contain only the customer-frontend q-* theme classes.
   */
  public egretThemes: ITheme[] = [
    {
      name: 'q-purple-dark-theme',
      baseColor: '#47056B',
      isActive: false,
    },
    {
      name: 'q-red-secondary-theme',
      baseColor: '#c42a04',
      isActive: false,
    },
    {
      name: 'q-teal-light-theme',
      baseColor: '#009fb8',
      isActive: false,
    },
  ];

  /**
   * Compatibility for bookmarked layout/theme query values from the old
   * Egret-based picker. These are aliases, not separate theme definitions.
   */
  private readonly legacyThemeAliases: Record<string, string> = {
    'egret-dark-purple': 'q-purple-dark-theme',
    'egret-light-purple': 'q-purple-dark-theme',
    'egret-navy': 'q-purple-dark-theme',
    'egret-dark-pink': 'q-red-secondary-theme',
    'egret-blue': 'q-teal-light-theme',
  };

  public activatedTheme: ITheme;

  constructor() {
    const rendererFactory = inject(RendererFactory2);
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  /** Resolve a current q-* name or migrate a legacy value to its replacement. */
  resolveThemeName(themeName?: string): string {
    if (!themeName) {
      return DEFAULT_THEME;
    }

    if (this.egretThemes.some((theme) => theme.name === themeName)) {
      return themeName;
    }

    return this.legacyThemeAliases[themeName] ?? DEFAULT_THEME;
  }

  // Invoked in AppComponent and apply 'activatedTheme' on startup.
  applyMatTheme(themeName?: string) {
    const queryThemeName = this.setThemeFromQuery();
    const resolvedThemeName = queryThemeName ?? this.resolveThemeName(themeName);

    this.flipActiveFlag(resolvedThemeName);
    this.applyBodyTheme(resolvedThemeName);
  }

  changeTheme(_prevTheme: string | undefined, themeName?: string) {
    const resolvedThemeName = this.resolveThemeName(themeName);
    this.applyBodyTheme(resolvedThemeName);
    this.flipActiveFlag(resolvedThemeName);
    this.onThemeChange.emit(this.activatedTheme);
  }

  flipActiveFlag(themeName: string) {
    this.activatedTheme = this.egretThemes.find((theme) => theme.name === themeName);
    this.egretThemes.forEach((theme) => {
      theme.isActive = theme.name === themeName;
    });
  }

  /**
   * Apply a theme class while ensuring only one customer theme remains on the
   * body. This also prevents an old Egret class from surviving a live switch.
   */
  private applyBodyTheme(themeName: string) {
    const themeClassNames = [
      ...this.egretThemes.map((theme) => theme.name),
      ...Object.keys(this.legacyThemeAliases),
    ];

    themeClassNames.forEach((className) => {
      this.renderer.removeClass(this.document.body, className);
    });
    if (themeName) {
      this.renderer.addClass(this.document.body, themeName);
    }
  }

  // Supports the legacy demo query parameter while resolving it to q-*.
  setThemeFromQuery(): string | undefined {
    const themeStr = getQueryParam('theme');

    try {
      const configuredTheme = JSON.parse(themeStr);
      if (typeof configuredTheme?.name !== 'string') {
        return undefined;
      }

      const resolvedThemeName = this.resolveThemeName(configuredTheme.name);
      this.flipActiveFlag(resolvedThemeName);
      return resolvedThemeName;
    } catch {
      return undefined;
    }
  }
}
